import { getApiEndpoint } from '../config/api.js';

export interface Impact {
  type: 'market' | 'sector' | 'stock';
  title: string;
  description: string;
  confidence: number;
  impact: 'high' | 'medium' | 'low';
  affectedStocks?: string[];
}

export interface ImpactAnalysisData {
  impacts: Impact[];
  marketSentiment: 'bullish' | 'bearish' | 'neutral';
  lastUpdated: string;
  summary: string;
}

export const impactAnalysisApi = {
  async getImpactAnalysis(
    profileId?: string,
    opts?: { wid?: string; reason?: string; focusSymbol?: string; fast?: boolean; model?: string; timeoutMs?: number }
  ): Promise<ImpactAnalysisData> {
    const base = getApiEndpoint();
    const url = profileId
      ? new URL(`${base}/profile/${encodeURIComponent(profileId)}/impact`)
      : new URL(`${base}/impactAnalysis`);
    if (opts?.wid) url.searchParams.set('wid', opts.wid);
    if (opts?.reason) url.searchParams.set('reason', opts.reason);
    if (opts?.focusSymbol) url.searchParams.set('focusSymbol', opts.focusSymbol);
    // Favor fast path by default to avoid API Gateway 504 timeouts
    url.searchParams.set('fast', String(opts?.fast ?? true));
    if (opts?.model) url.searchParams.set('model', opts.model);
    if (typeof opts?.timeoutMs === 'number') url.searchParams.set('timeoutMs', String(Math.max(4000, Math.min(25000, Math.floor(opts.timeoutMs)))));
    const reqUrl = url.toString();
    try {
      console.log('[impactAnalysisApi] GET', {
        url: reqUrl,
        profileId: profileId || null,
        wid: opts?.wid,
        reason: opts?.reason,
        focusSymbol: opts?.focusSymbol || null,
      });
    } catch {}
    const response = await fetch(reqUrl);
    
    if (!response.ok) {
      throw new Error(`Impact analysis API error: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json() as { data: ImpactAnalysisData };
    try {
      console.log('[impactAnalysisApi] OK', {
        profileId: profileId || null,
        wid: opts?.wid,
        reason: opts?.reason,
        impacts: Array.isArray(data?.data?.impacts) ? data.data.impacts.length : 'n/a',
        marketSentiment: (data?.data as any)?.marketSentiment,
      });
    } catch {}
    return data.data;
  },

  async createJob(
    profileId: string,
    opts?: { focusSymbol?: string; fast?: boolean; model?: string; timeoutMs?: number }
  ): Promise<{ jobId: string; status: string }> {
    const base = getApiEndpoint();
    const url = new URL(`${base}/profile/${encodeURIComponent(profileId)}/impact`);
    url.searchParams.set('async', '1');
    if (opts?.focusSymbol) url.searchParams.set('focusSymbol', opts.focusSymbol);
    if (opts?.model) url.searchParams.set('model', opts.model);
    url.searchParams.set('fast', String(opts?.fast ?? false));
    if (typeof opts?.timeoutMs === 'number') url.searchParams.set('timeoutMs', String(Math.max(4000, Math.min(25000, Math.floor(opts.timeoutMs)))));
    const res = await fetch(url.toString());
    if (!res.ok) throw new Error(`Failed to create impact analysis job: ${res.status}`);
    return res.json();
  },

  async getJobStatus(
    profileId: string,
    jobId: string
  ): Promise<{ jobId: string; status: string; lastUpdated?: string; data?: ImpactAnalysisData; error?: string }> {
    const base = getApiEndpoint();
    const url = new URL(`${base}/profile/${encodeURIComponent(profileId)}/impact`);
    url.searchParams.set('jobId', jobId);
    const res = await fetch(url.toString());
    if (!res.ok) throw new Error(`Failed to fetch job status: ${res.status}`);
    return res.json();
  },

  async getImpactAnalysisAsync(
    profileId: string,
    opts?: { wid?: string; reason?: string; focusSymbol?: string; fast?: boolean; model?: string; timeoutMs?: number }
  ): Promise<{ kind: 'data'; data: ImpactAnalysisData } | { kind: 'job'; jobId: string; status: string }>
  {
    const base = getApiEndpoint();
    const url = new URL(`${base}/profile/${encodeURIComponent(profileId)}/impact`);
    url.searchParams.set('async', '1');
    if (opts?.wid) url.searchParams.set('wid', opts.wid);
    if (opts?.reason) url.searchParams.set('reason', opts.reason);
    if (opts?.focusSymbol) url.searchParams.set('focusSymbol', opts.focusSymbol);
    // Deep analysis by default in async path
    url.searchParams.set('fast', String(opts?.fast ?? false));
    if (opts?.model) url.searchParams.set('model', opts.model);
    if (typeof opts?.timeoutMs === 'number') url.searchParams.set('timeoutMs', String(Math.max(4000, Math.min(25000, Math.floor(opts.timeoutMs)))));
    const res = await fetch(url.toString());
    if (res.status === 200) {
      const payload = await res.json() as { data: ImpactAnalysisData };
      return { kind: 'data', data: payload.data };
    }
    if (res.status === 202) {
      const payload = await res.json() as { jobId: string; status: string };
      return { kind: 'job', jobId: payload.jobId, status: payload.status };
    }
    if (res.status === 501) {
      throw new Error('Async impact analysis not enabled');
    }
    throw new Error(`Async impact analysis error: ${res.status}`);
  },
};
